"use client";

import * as React from "react";
import { useSearchParams } from "next/navigation";
import { ImagePlus, Clapperboard, Send, Loader2, Paperclip, X, Download, Clock, CheckCircle2, XCircle, Eye, Copy, Check, ZoomIn } from "lucide-react";
import { Button, Textarea, Alert, Badge, Modal } from "@/components/ui";
import { apiGet, ApiClientError } from "@/lib/api-client";
import { cn } from "@/lib/cn";
import { useAuthModal } from "@/features/auth/auth-modal";
import type { AiGenRequestDTO, AiGenKind } from "@/types/ai-gen";

const MAX_LEN = 4000;

export function AiGenStudio({ isLoggedIn = true }: { isLoggedIn?: boolean }) {
  const searchParams = useSearchParams();
  const { openAuth } = useAuthModal();
  const [kind, setKind] = React.useState<AiGenKind>("image");
  // Prefill prompt khi mở từ "Kho tài nguyên → Prompt AI" (?prompt=...).
  const [prompt, setPrompt] = React.useState(() => (searchParams.get("prompt") ?? "").slice(0, MAX_LEN));
  const [files, setFiles] = React.useState<File[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<AiGenRequestDTO[]>([]);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const pendingSubmit = React.useRef(false);

  const load = React.useCallback(async () => {
    if (!isLoggedIn) return; // khách chưa đăng nhập → không có danh sách để tải
    try {
      const r = await apiGet<{ items: AiGenRequestDTO[] }>("/api/ai-gen/mine");
      setItems(r.items);
    } catch {
      /* giữ danh sách cũ nếu lỗi mạng */
    }
  }, [isLoggedIn]);

  React.useEffect(() => {
    void load();
  }, [load]);

  // Tự cập nhật kết quả: 10s/lần KHI còn yêu cầu đang chờ (không reload trang).
  React.useEffect(() => {
    if (!items.some((i) => i.status === "pending")) return;
    const t = setInterval(() => void load(), 10000);
    return () => clearInterval(t);
  }, [items, load]);

  // Vừa đăng nhập xong (prop đổi false→true) mà đang có yêu cầu chờ → tự gửi.
  React.useEffect(() => {
    if (isLoggedIn && pendingSubmit.current) {
      pendingSubmit.current = false;
      void submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  async function submit() {
    const text = prompt.trim();
    if (!text || submitting) return;
    // Chưa đăng nhập → mở modal đăng nhập/đăng ký, chưa gửi yêu cầu.
    if (!isLoggedIn) {
      pendingSubmit.current = true;
      openAuth("register");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.set("kind", kind);
      fd.set("prompt", text);
      for (const f of files) fd.append("files", f);
      const res = await fetch("/api/ai-gen", { method: "POST", body: fd });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json || json.error) {
        throw new ApiClientError(json?.error ?? { code: "err", message: "Không gửi được yêu cầu." });
      }
      setItems((prev) => [json.data as AiGenRequestDTO, ...prev]);
      setPrompt("");
      setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Không gửi được yêu cầu. Thử lại nhé.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-h3 text-foreground">Tạo ảnh & video bằng AI</h1>
        <p className="mt-1 text-body-sm text-muted">
          Mô tả điều bạn muốn, hệ thống sẽ tạo giúp bạn. Kết quả hiện ngay tại đây khi hoàn tất.
        </p>
      </div>

      {/* Form tạo */}
      <div className="space-y-3 rounded-2xl border border-border bg-surface p-5">
        <div className="inline-flex gap-1 rounded-lg bg-subtle p-1">
          {([["image", "Ảnh", ImagePlus], ["video", "Video", Clapperboard]] as const).map(([k, label, Icon]) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-body-sm font-semibold transition-colors",
                kind === k ? "bg-surface text-foreground shadow-sm" : "text-muted"
              )}
            >
              <Icon className="size-4" aria-hidden="true" /> {label}
            </button>
          ))}
        </div>

        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value.slice(0, MAX_LEN))}
          placeholder={
            kind === "image"
              ? "VD: Vẽ hình con mèo hoạt hình dễ thương cầm quyển vở, phong cách màu nước…"
              : "VD: Video ngắn hoạt hình một bạn nhỏ vẫy tay chào, nền lớp học vui tươi…"
          }
          rows={4}
        />

        {/* Ảnh mẫu (tuỳ chọn) */}
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            multiple
            className="sr-only"
            onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, 4))}
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border-strong px-3 py-1.5 text-caption font-medium text-foreground-secondary hover:border-primary hover:text-primary"
            >
              <Paperclip className="size-3.5" aria-hidden="true" /> Đính kèm ảnh mẫu (tuỳ chọn)
            </button>
            {files.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-full bg-subtle px-2.5 py-1 text-caption text-foreground-secondary">
                {f.name.length > 22 ? f.name.slice(0, 21) + "…" : f.name}
                <button type="button" onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} aria-label="Bỏ">
                  <X className="size-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="flex items-center justify-between">
          <span className="text-caption text-muted">{prompt.length}/{MAX_LEN}</span>
          <Button onClick={submit} isLoading={submitting} disabled={!prompt.trim()}>
            <Send className="size-4" aria-hidden="true" /> Tạo {kind === "video" ? "video" : "ảnh"}
          </Button>
        </div>
      </div>

      {/* Danh sách yêu cầu — chỉ hiện khi đã đăng nhập */}
      {isLoggedIn && (
        <div className="space-y-3">
          {items.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-border py-10 text-center text-body-sm text-muted">
              Chưa có yêu cầu nào. Hãy nhập mô tả ở trên để bắt đầu.
            </p>
          ) : (
            items.map((it) => <RequestCard key={it.id} req={it} />)
          )}
        </div>
      )}
    </div>
  );
}

/** Ảnh kết quả: hover chuột để phóng to xem (không mở tab mới). */
function ResultImage({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="group/zoom relative cursor-zoom-in">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={src} alt={alt} loading="lazy" className="max-h-[52vh] w-full bg-white object-contain" />
      <span className="pointer-events-none absolute right-2 top-2 flex items-center gap-1 rounded-full bg-black/55 px-2 py-1 text-caption text-white opacity-0 transition-opacity group-hover/zoom:opacity-100">
        <ZoomIn className="size-3.5" aria-hidden="true" /> Rê chuột để xem to
      </span>
      {/* Lớp phóng to toàn màn hình khi hover (chỉ máy có chuột). pointer-events-none để không cướp hover. */}
      <div className="pointer-events-none fixed inset-0 z-[90] hidden place-items-center bg-black/80 p-6 sm:group-hover/zoom:grid">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={src} alt={alt} className="max-h-[90vh] max-w-[92vw] rounded-lg object-contain shadow-2xl" />
      </div>
    </div>
  );
}

/** Băng "đang tạo": sau 1 phút chờ thì đổi sang lời trấn an thầy cô. */
function PendingBanner({ createdAt }: { createdAt: string }) {
  const created = new Date(createdAt).getTime();
  const [late, setLate] = React.useState(() => Date.now() - created >= 60_000);

  React.useEffect(() => {
    if (late) return;
    const remaining = 60_000 - (Date.now() - created);
    if (remaining <= 0) {
      setLate(true);
      return;
    }
    const t = setTimeout(() => setLate(true), remaining);
    return () => clearTimeout(t);
  }, [late, created]);

  return (
    <div
      className={cn(
        "flex items-center gap-2.5 rounded-xl px-3.5 py-3 text-body-sm font-medium",
        late
          ? "bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 dark:from-amber-950/40 dark:to-orange-950/40 dark:text-amber-300"
          : "bg-gradient-to-r from-cyan-50 to-blue-50 text-blue-700 dark:from-cyan-950/40 dark:to-blue-950/40 dark:text-cyan-300"
      )}
    >
      <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden="true" />
      {late
        ? "Thầy cô chờ thêm chút, do nhiều thầy cô đang cùng sử dụng nên AI sẽ cần thêm thời gian để thực hiện… Sắp có rồi ạ !!!"
        : "AI đang tạo, vui lòng chờ… Kết quả sẽ tự hiện ở đây."}
    </div>
  );
}

/** Prompt hiển thị gọn (2 dòng); prompt dài → mở popup xem chi tiết (thẻ luôn gọn). */
function PromptText({ text }: { text: string }) {
  const [open, setOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const isLong = text.length > 110 || text.includes("\n");

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* bỏ qua */
    }
  }

  return (
    <div>
      <p className="line-clamp-2 text-body-sm leading-relaxed text-foreground">{text}</p>
      {isLong && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-0.5 inline-flex items-center gap-1 text-caption font-medium text-primary hover:underline"
        >
          <Eye className="size-3.5" aria-hidden="true" />
          Xem chi tiết
        </button>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="md"
        title="Nội dung prompt"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={copy}>
              {copied ? <Check className="size-4" aria-hidden="true" /> : <Copy className="size-4" aria-hidden="true" />}
              {copied ? "Đã chép" : "Sao chép"}
            </Button>
            <Button variant="tertiary" onClick={() => setOpen(false)}>
              Đóng
            </Button>
          </div>
        }
      >
        <div className="max-h-[60vh] overflow-y-auto whitespace-pre-line break-words rounded-xl bg-subtle px-4 py-3 text-body-sm leading-relaxed text-foreground">
          {text}
        </div>
      </Modal>
    </div>
  );
}

function RequestCard({ req }: { req: AiGenRequestDTO }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-surface">
      <div className="flex items-center gap-2 border-b border-border/60 px-4 py-2.5">
        <Badge variant="neutral">{req.kind === "video" ? "🎬 Video" : "🖼️ Ảnh"}</Badge>
        <StatusBadge status={req.status} />
        <span className="ml-auto text-caption text-muted">
          {new Date(req.createdAt).toLocaleString("vi-VN", { hour12: false, dateStyle: undefined })}
        </span>
      </div>

      <div className="space-y-3 p-4">
        <PromptText text={req.prompt} />

        {req.status === "pending" && <PendingBanner createdAt={req.createdAt} />}

        {req.status === "rejected" && (
          <p className="rounded-xl bg-danger/10 px-3.5 py-2.5 text-body-sm text-danger">
            Yêu cầu không thực hiện được{req.adminNote ? `: ${req.adminNote}` : "."}
          </p>
        )}

        {req.status === "done" && (
          <div className="space-y-3">
            {req.adminNote && (
              <p className="rounded-lg bg-subtle px-3 py-2 text-body-sm text-foreground-secondary">
                💬 {req.adminNote}
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              {req.results.map((r, i) => (
                <div key={i} className="overflow-hidden rounded-xl border border-border bg-subtle">
                  {r.contentType.startsWith("video/") ? (
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    <video src={r.url} controls className="aspect-video w-full bg-black" />
                  ) : (
                    <ResultImage src={r.previewUrl ?? r.url} alt={`Kết quả ${i + 1}`} />
                  )}
                  <a
                    href={`${r.url}&dl=1`}
                    download={r.name || undefined}
                    className="flex items-center justify-center gap-1.5 border-t border-border py-2.5 text-caption font-semibold text-primary hover:bg-surface"
                  >
                    <Download className="size-3.5" aria-hidden="true" /> Tải ảnh chất lượng cao
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: AiGenRequestDTO["status"] }) {
  if (status === "done")
    return (
      <Badge variant="success">
        <CheckCircle2 className="mr-1 inline size-3" /> Hoàn tất
      </Badge>
    );
  if (status === "rejected")
    return (
      <Badge variant="danger">
        <XCircle className="mr-1 inline size-3" /> Từ chối
      </Badge>
    );
  return (
    <Badge variant="warning">
      <Clock className="mr-1 inline size-3" /> Đang tạo
    </Badge>
  );
}
